import { useEffect, useRef, useState } from "react";
import { api } from "../api/apiClient";
import type {
  ConfirmDeliveryLinePayload,
  DeliveryLineStatus,
  ScheduledDeliveryLine,
} from "../api/types";
import { cost, money, percentText } from "../i18n/intl";
import { CaptureLineBarcode } from "./deliveries/CaptureLineBarcode";
import { differencePayload } from "../utils/deliveryDifference";

interface Props {
  line: ScheduledDeliveryLine;
  canConfirm: boolean;
  /** G-59: the line a scan or typed code just found. */
  highlighted?: boolean;
  /** G-59: the last scan that matched nothing, offered to "Add barcode from the box". */
  unmatchedCode?: string | null;
  /** G-121 S2: `photo`, when present, is of the units that arrived damaged. */
  onConfirm: (payload: ConfirmDeliveryLinePayload, photo?: File | null) => Promise<void>;
  /** G-59: barcode capture at first receipt. */
  onCaptureBarcode?: (barcode: string) => Promise<void>;
  /** G-131: owner or manager — undo this line's receiving while its stock is untouched. */
  onUndoReceive?: () => void;
}

const fmtQty = (q: string | null) => (q === null ? "—" : Number(q).toString());

/** A decimal string off the wire as a number; null and the unparseable count as 0. */
const qty = (q: string | null) => {
  if (q === null) return 0;
  const n = Number(q);
  return Number.isFinite(n) ? n : 0;
};

function statusBadge(status: DeliveryLineStatus) {
  const map: Record<DeliveryLineStatus, { className: string; label: string }> = {
    PENDING: { className: "badge muted", label: "Pending" },
    CONFIRMED_AS_EXPECTED: { className: "badge success", label: "Exact match" },
    CONFIRMED_WITH_DIFFERENCE: {
      className: "badge primary",
      label: "Confirmed (variance)",
    },
    MISSING: { className: "badge danger", label: "Missing" },
  };
  const { className, label } = map[status];
  return <span className={className}>{label}</span>;
}

/**
 * G-121 S2: the photo of what arrived damaged, fetched only when asked for.
 * The route is Bearer-guarded, so a plain `<a href>` would come back 401 —
 * this follows the house pattern (`TaskImages`): an auth-gated blob turned
 * into an object URL, revoked when the figure goes away.
 */
function MovementPhoto({ movementId }: { movementId: string }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let created: string | null = null;
    void api.fetchMovementPhotoBlobUrl(movementId).then((loaded) => {
      if (cancelled) {
        if (loaded) URL.revokeObjectURL(loaded);
        return;
      }
      created = loaded;
      if (loaded) setUrl(loaded);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setUrl(null);
      setFailed(false);
    };
  }, [open, movementId]);

  return (
    <>
      {" "}·{" "}
      <button type="button" className="button plain" onClick={() => setOpen((v) => !v)}>
        {open ? "hide photo" : "photo"}
      </button>
      {open ? (
        url ? (
          <figure className="task-image-figure">
            <img src={url} alt="Damaged on arrival" className="task-image" />
          </figure>
        ) : (
          <span>{failed ? " photo unavailable" : " loading photo…"}</span>
        )
      ) : null}
    </>
  );
}

export function ConfirmDeliveryLineRow({
  line,
  canConfirm,
  highlighted = false,
  unmatchedCode = null,
  onConfirm,
  onCaptureBarcode,
  onUndoReceive,
}: Props) {
  const [openDifference, setOpenDifference] = useState(false);
  // Owner, 2026-10-01: the expected quantity is known, so the form asks only
  // how many are different, and whether those arrived damaged rather than not
  // at all — the same form as the shop tablet.
  const [differenceQty, setDifferenceQty] = useState("");
  const [damaged, setDamaged] = useState(false);
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const libraryInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<"exact" | "diff" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitExact = async () => {
    setError(null);
    setBusy("exact");
    try {
      await onConfirm({ asExpected: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not confirm");
    } finally {
      setBusy(null);
    }
  };

  const submitDifference = async () => {
    setError(null);
    const result = differencePayload({
      expectedQty: line.expectedQty,
      count: differenceQty,
      damaged,
      hasPhoto: photo !== null,
    });
    if (!result.ok) {
      setError(
        result.problem === "count"
          ? "Enter how many."
          : result.problem === "tooMany"
            ? `That is more than the ${fmtQty(line.expectedQty)} expected.`
            : "Take or choose a photo of the damage.",
      );
      return;
    }
    setBusy("diff");
    try {
      await onConfirm(
        {
          ...result.payload,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        damaged ? photo : null,
      );
      setOpenDifference(false);
      setDifferenceQty("");
      setDamaged(false);
      setNotes("");
      setPhoto(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not confirm");
    } finally {
      setBusy(null);
    }
  };

  const isPending = line.status === "PENDING";
  const uom = line.item.defaultUom;
  const damagedCount = qty(line.damagedQty);
  const shortageEur = qty(line.money.shortageEur);
  const overageEur = qty(line.money.overageEur);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        background: "var(--color-surface-muted)",
      }}
      className={highlighted ? "delivery-line-found" : undefined}
      aria-current={highlighted ? "true" : undefined}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{line.item.name}</div>
          <div className="muted" style={{ fontSize: 13 }}>
            {/* G-121 S2: the paper's figure leads when the line has one; a
                typed line shows what was expected. */}
            {line.billedTotalEur !== null ? (
              <>
                Billed {fmtQty(line.billedQty)} {uom} ×{" "}
                {cost(line.money.billedUnitPriceEur)} = {money(line.billedTotalEur)}
              </>
            ) : (
              <>
                Expected {fmtQty(line.expectedQty)} {uom} ×{" "}
                {cost(line.expectedUnitCostEur)} = {money(line.expectedTotalEur)}
              </>
            )}
            {/* G-121 S3: say which side of IVA the figure is on — only when the
                line knows (old lines of unknown basis claim nothing). */}
            {line.moneyBasis !== null ? " before IVA" : ""}
            {line.expectedGrossTotalEur !== null && line.ivaRatePercent !== null
              ? ` · ${money(line.expectedGrossTotalEur)} with IVA ${percentText(line.ivaRatePercent)} %`
              : ""}{" "}
            · zone {line.zone.name} ({line.zone.zoneType})
            {line.item.isPerishable ? " · perishable" : ""}
            {line.item.barcode ? ` · barcode ${line.item.barcode}` : " · no barcode yet"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {statusBadge(line.status)}
        </div>
      </div>

      {!isPending ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Received {fmtQty(line.receivedQty)} of{" "}
          {fmtQty(line.billedQty ?? line.expectedQty)}
          {damagedCount > 0 ? ` · ${fmtQty(line.damagedQty)} damaged` : ""}
          {line.batch ? ` · ${cost(line.batch.purchaseUnitCostEur)}/u` : ""}
          {shortageEur > 0 ? ` · not received ${money(line.money.shortageEur)}` : ""}
          {overageEur > 0 ? ` · extra ${money(line.money.overageEur)}` : ""}
          {line.batch ? (
            <>
              {" "}· batch <code>{line.batch.batchCode}</code>
            </>
          ) : null}
          {line.confirmedBy ? (
            <>
              {" "}· confirmed by {line.confirmedBy.fullName}
              {line.confirmedAt
                ? ` at ${new Date(line.confirmedAt).toLocaleString()}`
                : ""}
            </>
          ) : null}
          {line.batch?.damagePhotoMovementId ? (
            <MovementPhoto movementId={line.batch.damagePhotoMovementId} />
          ) : null}
          {line.notes ? <div>Notes: {line.notes}</div> : null}
          {/* G-131: the server says whether this receive can still be undone, and why not. */}
          {onUndoReceive ? (
            line.canUndoReceive ? (
              <div className="dlv-line-undo">
                <button type="button" className="button plain" onClick={onUndoReceive}>
                  Undo receiving
                </button>
              </div>
            ) : line.undoBlockedBy ? (
              <div className="dlv-line-undo">Undo receiving is closed: {line.undoBlockedBy} Adjust the stock instead.</div>
            ) : null
          ) : null}
        </div>
      ) : null}

      {isPending && canConfirm ? (
        <>
          {error ? <div className="alert error">{error}</div> : null}
          <div
            style={{
              display: "flex",
              gap: "var(--space-3)",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="button"
              onClick={() => void submitExact()}
              // Off while the difference form is open (owner, 2026-10-01):
              // a typed difference is saved with Save, never with this.
              disabled={busy !== null || openDifference}
              style={{
                background: "var(--color-success)",
                borderColor: "var(--color-success)",
                minWidth: 200,
                fontSize: 16,
              }}
            >
              {busy === "exact" ? "Confirming…" : "+ Confirm exact"}
            </button>
            <button
              type="button"
              className="button tinted"
              onClick={() => setOpenDifference((v) => !v)}
              disabled={busy !== null}
            >
              {openDifference ? "Close" : "Difference"}
            </button>
            {!line.item.barcode && onCaptureBarcode ? (
              <CaptureLineBarcode
                lineId={line.id}
                itemName={line.item.name}
                disabled={busy !== null}
                suggestedCode={unmatchedCode ?? undefined}
                onCapture={onCaptureBarcode}
              />
            ) : null}
          </div>

          {openDifference ? (
            <div
              className="form-grid"
              style={{
                padding: "var(--space-3)",
                borderRadius: "var(--radius-md)",
                background: "var(--color-surface)",
              }}
            >
              <div className="field full">
                <label htmlFor={`difference-${line.id}`}>
                  {damaged ? "How many arrived damaged?" : "How many are missing?"}
                </label>
                <input
                  id={`difference-${line.id}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={line.expectedQty}
                  step="0.001"
                  value={differenceQty}
                  onChange={(e) => setDifferenceQty(e.target.value)}
                />
                <label className="delivery-damaged-toggle">
                  <input
                    type="checkbox"
                    checked={damaged}
                    onChange={(e) => {
                      setDamaged(e.target.checked);
                      // A photo only belongs to damaged units.
                      if (!e.target.checked) setPhoto(null);
                    }}
                  />
                  <span>They arrived damaged</span>
                </label>
              </div>
              {damaged ? (
                <div
                  className="field full"
                  role="group"
                  aria-labelledby={`damage-photo-${line.id}`}
                >
                  {/* Not a <label>: both file inputs are hidden, so the caption
                      names the group of buttons instead (review finding 9). */}
                  <span id={`damage-photo-${line.id}`} className="field-caption">
                    Photo of the damage (required)
                  </span>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => {
                      setPhoto(e.target.files?.[0] ?? null);
                      e.target.value = "";
                    }}
                  />
                  <input
                    ref={libraryInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      setPhoto(e.target.files?.[0] ?? null);
                      e.target.value = "";
                    }}
                  />
                  <div className="waste-photo-actions">
                    <button
                      type="button"
                      className="button tinted"
                      disabled={busy !== null}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      Take photo
                    </button>
                    <button
                      type="button"
                      className="button plain"
                      disabled={busy !== null}
                      onClick={() => libraryInputRef.current?.click()}
                    >
                      Choose file
                    </button>
                    {photo ? (
                      <button
                        type="button"
                        className="button plain"
                        disabled={busy !== null}
                        onClick={() => setPhoto(null)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <p className="field-hint">
                    {photo
                      ? `Attached: ${photo.name}`
                      : "They go into stock as usual; the photo stays with the delivery."}
                  </p>
                </div>
              ) : null}
              <div className="field full">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  maxLength={500}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. box crushed in transit"
                />
              </div>
              <div className="button-row full">
                <button
                  type="button"
                  className="button"
                  onClick={() => void submitDifference()}
                  disabled={busy !== null}
                >
                  {busy === "diff" ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
